import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import React, { forwardRef } from 'react'

export function InputGroup({ children }: React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      data-slot="control"
      className={clsx(
        'relative isolate block',
        '[&>:not(:first-child)_input]:has-[[data-slot=icon]:first-child]:pl-10',
        'sm:[&>:not(:first-child)_input]:has-[[data-slot=icon]:first-child]:pl-8',
        '[&_input]:has-[[data-slot=icon]:last-child]:pr-10',
        'sm:[&_input]:has-[[data-slot=icon]:last-child]:pr-8',
        '[&>[data-slot=icon]]:pointer-events-none [&>[data-slot=icon]]:absolute [&>[data-slot=icon]]:top-3 [&>[data-slot=icon]]:z-10 [&>[data-slot=icon]]:size-5 sm:[&>[data-slot=icon]]:top-2.5 sm:[&>[data-slot=icon]]:size-4',
        '[&>[data-slot=icon]:first-child]:left-3 sm:[&>[data-slot=icon]:first-child]:left-2.5 [&>[data-slot=icon]:last-child]:right-3 sm:[&>[data-slot=icon]:last-child]:right-2.5',
        '[&>[data-slot=icon]]:text-zinc-500 dark:[&>[data-slot=icon]]:text-zinc-400'
      )}
    >
      {children}
    </span>
  )
}

const dateTypes = ['date', 'datetime-local', 'month', 'time', 'week']
type DateType = (typeof dateTypes)[number]


export interface InputProps extends Omit<Headless.InputProps, 'as' | 'className'> {
    className?: string;
    type?: 'email' | 'number' | 'password' | 'search' | 'tel' | 'text' | 'url' | DateType;
    /** Show increment/decrement spinners for number inputs (default: false) */
    showSpinners?: boolean;
    /** Prefix text to display before the input value (e.g., "$") */
    prefix?: string;
    /** Suffix text to display after the input value (e.g., "ft", "'", "\"") */
    suffix?: string;
}

export const Input = forwardRef(function Input(
  {
    className,
    showSpinners = false,
    prefix,
    suffix,
    ...props
  }: InputProps,
  ref: React.ForwardedRef<HTMLInputElement>
) {
  return (
    <span
      data-slot="control"
      className={clsx([
        className,
        // Basic layout
        'relative block w-full',
        // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
        'before:pointer-events-none before:absolute before:inset-px before:rounded-[calc(theme(borderRadius.lg)-1px)] before:bg-white before:shadow',
        // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
        'dark:before:hidden',
        // Focus ring
        'after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-inset after:ring-transparent sm:after:focus-within:ring-2 sm:after:focus-within:ring-blue-500',
        // Disabled state
        'has-[[data-disabled]]:opacity-50 before:has-[[data-disabled]]:bg-zinc-950/5 before:has-[[data-disabled]]:shadow-none',
        // Invalid state
        'before:has-[[data-invalid]]:shadow-red-500/10',
      ])}
    >
      {prefix && (
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 dark:text-zinc-400 text-sm z-10">
          {prefix}
        </span>
      )}
      {suffix && (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 dark:text-zinc-400 text-sm z-10">
          {suffix}
        </span>
      )}
      {props.type && dateTypes.includes(props.type) ? (
        <input
          ref={ref}
          {...props}
          className={clsx([
            // Date-specific styles
            '[&::-webkit-datetime-edit-fields-wrapper]:p-0',
            '[&::-webkit-date-and-time-value]:min-h-[1.5em]',
            '[&::-webkit-datetime-edit]:inline-flex',
            '[&::-webkit-datetime-edit]:p-0',
            '[&::-webkit-calendar-picker-indicator]:cursor-pointer',
            '[&::-webkit-calendar-picker-indicator]:opacity-100',
            '[&::-webkit-calendar-picker-indicator]:hover:opacity-70',
            // Basic layout
            'relative block w-full rounded-lg py-[calc(theme(spacing[2.5])-1px)] sm:py-[calc(theme(spacing[1.5])-1px)]',
            'px-[calc(theme(spacing[3.5])-1px)] sm:px-[calc(theme(spacing[3])-1px)]',
            // Typography
            'text-base/6 text-zinc-950 sm:text-sm/6 dark:text-white',
            // Border
            'border border-zinc-200 hover:border-zinc-300 dark:border-zinc-600 dark:hover:border-zinc-500',
            // Background
            'bg-transparent dark:bg-white/5',
            // Focus
            'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-0',
            // Dark mode
            'dark:[color-scheme:dark]',
          ])}
        />
      ) : (
        <Headless.Input
          ref={ref}
          {...props}
          className={clsx([
            // Number input - hide spinners unless showSpinners is true
            props.type === 'number' && !showSpinners && [
              '[appearance:textfield]',
              '[&::-webkit-outer-spin-button]:appearance-none',
              '[&::-webkit-inner-spin-button]:appearance-none',
            ],
          // Basic layout
          'relative block w-full appearance-none rounded-lg py-[calc(theme(spacing[2.5])-1px)] sm:py-[calc(theme(spacing[1.5])-1px)]',
          // Padding - adjust for prefix/suffix
          prefix && suffix ? 'pl-7 pr-7 sm:pl-6 sm:pr-6' :
          prefix ? 'pl-7 pr-[calc(theme(spacing[3.5])-1px)] sm:pl-6 sm:pr-[calc(theme(spacing[3])-1px)]' :
          suffix ? 'pl-[calc(theme(spacing[3.5])-1px)] pr-7 sm:pl-[calc(theme(spacing[3])-1px)] sm:pr-6' :
          'px-[calc(theme(spacing[3.5])-1px)] sm:px-[calc(theme(spacing[3])-1px)]',
          // Typography
          'text-base/6 text-zinc-950 placeholder:text-zinc-400 sm:text-sm/6 dark:text-white',
          // Border
          'border border-zinc-200 data-[hover]:border-zinc-950/20 dark:border-zinc-600 dark:data-[hover]:border-white/20',
          // Background color
          'bg-transparent dark:bg-white/5',
          // Hide default focus styles
          'focus:outline-none',
          // Invalid state
          'data-[invalid]:border-red-500 data-[invalid]:data-[hover]:border-red-500 data-[invalid]:dark:border-red-500 data-[invalid]:data-[hover]:dark:border-red-500',
          // Disabled state
          'data-[disabled]:border-zinc-950/20 dark:data-[hover]:data-[disabled]:border-white/15 data-[disabled]:dark:border-white/15 data-[disabled]:dark:bg-white/[2.5%]',
          // System icons
          'dark:[color-scheme:dark]',
        ])}
        />
      )}
    </span>
  )
})
